// ============================================================
// View: Einzelbuchungen — filterbare Tabelle mit Pagination
// ============================================================

import { useMemo, useState } from 'react';
import { Card, EurCell, NetCell, StatusBadge } from '../components/ui';
import { CO } from '../theme';
import { useApp } from '../store';
import { inRange, period } from '../aggregator';
import { dstr, eur, fmtNum, tstr } from '../format';
import { artColor } from '../theme';
import type { Eintrag, SpielStatus } from '../types';

type OutcomeFilter = 'alle' | 'pay' | 'nopay' | 'netpos' | 'netneg';

export function Einzelbuchungen() {
  const { history, periodMode, customFrom, customTo, setDetailId, detailId } = useApp();
  const work = history?.eintraege ?? [];

  const [tSearch, setTSearch] = useState('');
  const [tArt, setTArt] = useState('alle');
  const [tStatus, setTStatus] = useState('alle');
  const [tOutcome, setTOutcome] = useState<OutcomeFilter>('alle');
  const [tMin, setTMin] = useState('');
  const [tMax, setTMax] = useState('');
  const [tSort, setTSort] = useState<'iso' | 'spielart' | 'status' | 'einsatz' | 'gewinn' | 'netto'>('iso');
  const [tDir, setTDir] = useState<'asc' | 'desc'>('desc');
  const [tPage, setTPage] = useState(1);

  const arten = useMemo(() => Array.from(new Set(work.map((e) => e.spielart))).sort(), [work]);

  const p = useMemo(() => period(periodMode, work, customFrom, customTo), [periodMode, work, customFrom, customTo]);
  const fe = useMemo(() => (p.full ? work.slice() : work.filter((e) => inRange(e, p))), [work, p]);

  const rows = useMemo(() => {
    let r = fe.slice();
    if (tSearch) {
      const q = tSearch.toLowerCase();
      r = r.filter((e) => `${e.spielart} ${e.id} ${e.rohtext}`.toLowerCase().includes(q));
    }
    if (tArt !== 'alle') r = r.filter((e) => e.spielart === tArt);
    if (tStatus !== 'alle') r = r.filter((e) => e.status === (tStatus as SpielStatus));
    if (tOutcome === 'pay') r = r.filter((e) => e.gewinn > 0);
    else if (tOutcome === 'nopay') r = r.filter((e) => e.gewinn === 0);
    else if (tOutcome === 'netpos') r = r.filter((e) => e.gewinn > e.einsatz);
    else if (tOutcome === 'netneg') r = r.filter((e) => e.gewinn < e.einsatz);
    if (tMin !== '') r = r.filter((e) => e.einsatz >= parseFloat(tMin));
    if (tMax !== '') r = r.filter((e) => e.einsatz <= parseFloat(tMax));

    const dir = tDir === 'asc' ? 1 : -1;
    r.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (tSort) {
        case 'spielart':
          va = a.spielart;
          vb = b.spielart;
          break;
        case 'status':
          va = a.status;
          vb = b.status;
          break;
        case 'einsatz':
          va = a.einsatz;
          vb = b.einsatz;
          break;
        case 'gewinn':
          va = a.gewinn;
          vb = b.gewinn;
          break;
        case 'netto':
          va = a.gewinn - a.einsatz;
          vb = b.gewinn - b.einsatz;
          break;
        default:
          va = a.iso;
          vb = b.iso;
      }
      return va < vb ? -dir : va > vb ? dir : 0;
    });
    return r;
  }, [fe, tSearch, tArt, tStatus, tOutcome, tMin, tMax, tSort, tDir]);

  const per = 25;
  const pages = Math.max(1, Math.ceil(rows.length / per));
  const page = Math.min(tPage, pages);
  const pageRows = rows.slice((page - 1) * per, page * per);

  function clickHeader(key: typeof tSort) {
    if (!key) return;
    if (tSort === key) setTDir(tDir === 'desc' ? 'asc' : 'desc');
    else {
      setTSort(key);
      setTDir('desc');
    }
    setTPage(1);
  }

  const selStyle: React.CSSProperties = {
    padding: '7px 10px',
    border: `1px solid ${CO.border}`,
    borderRadius: 8,
    fontSize: 12.5,
    background: '#fff',
    color: CO.text,
    fontFamily: 'inherit',
  };

  function Th({
    label,
    keyName,
    align,
  }: {
    label: string;
    keyName?: typeof tSort;
    align?: 'left' | 'right';
  }) {
    const isActive = keyName === tSort;
    return (
      <th
        onClick={() => clickHeader(keyName!)}
        style={{
          textAlign: align || 'left',
          fontWeight: 600,
          padding: '10px 10px',
          color: isActive ? CO.text : CO.muted,
          cursor: keyName ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          position: 'sticky',
          top: 0,
          background: '#f6f7f4',
          borderBottom: `1px solid ${CO.border}`,
          zIndex: 1,
        }}
      >
        <span>
          {label}
          {keyName ? (
            <span
              style={{
                fontSize: 9,
                marginLeft: 4,
                color: isActive ? CO.sidebar : 'transparent',
              }}
            >
              {isActive ? (tDir === 'asc' ? '▲' : '▼') : '▲'}
            </span>
          ) : null}
        </span>
      </th>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={{ fontSize: 11.5, color: CO.muted, display: 'block', marginBottom: 4 }}>
              Suche (Spielart, ID, Ergebnistext)
            </label>
            <input
              value={tSearch}
              onChange={(e) => {
                setTSearch(e.target.value);
                setTPage(1);
              }}
              placeholder="Suchen …"
              style={{ ...selStyle, width: '100%' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11.5, color: CO.muted, display: 'block', marginBottom: 4 }}>Spielart</label>
            <select
              value={tArt}
              onChange={(e) => {
                setTArt(e.target.value);
                setTPage(1);
              }}
              style={selStyle}
            >
              <option value="alle">Alle Spielarten</option>
              {arten.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11.5, color: CO.muted, display: 'block', marginBottom: 4 }}>Status</label>
            <select
              value={tStatus}
              onChange={(e) => {
                setTStatus(e.target.value);
                setTPage(1);
              }}
              style={selStyle}
            >
              <option value="alle">Alle Status</option>
              <option value="teilnehmend">teilnehmend</option>
              <option value="in Auswertung">in Auswertung</option>
              <option value="abgeschlossen">abgeschlossen</option>
              <option value="archiviert">archiviert</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11.5, color: CO.muted, display: 'block', marginBottom: 4 }}>Ergebnis</label>
            <select
              value={tOutcome}
              onChange={(e) => {
                setTOutcome(e.target.value as OutcomeFilter);
                setTPage(1);
              }}
              style={selStyle}
            >
              <option value="alle">Alle</option>
              <option value="pay">Mit Auszahlung</option>
              <option value="nopay">Ohne Auszahlung</option>
              <option value="netpos">Positives Nettoergebnis</option>
              <option value="netneg">Negatives Nettoergebnis</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11.5, color: CO.muted, display: 'block', marginBottom: 4 }}>
              Einsatz von – bis (€)
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="number"
                step="0.01"
                value={tMin}
                onChange={(e) => {
                  setTMin(e.target.value);
                  setTPage(1);
                }}
                placeholder="min"
                style={{ ...selStyle, width: 72 }}
              />
              <input
                type="number"
                step="0.01"
                value={tMax}
                onChange={(e) => {
                  setTMax(e.target.value);
                  setTPage(1);
                }}
                placeholder="max"
                style={{ ...selStyle, width: 72 }}
              />
            </div>
          </div>
          <button
            onClick={() => {
              setTSearch('');
              setTArt('alle');
              setTStatus('alle');
              setTOutcome('alle');
              setTMin('');
              setTMax('');
              setTPage(1);
            }}
            style={{
              padding: '8px 13px',
              border: `1px solid ${CO.border}`,
              borderRadius: 8,
              background: '#fff',
              color: CO.muted,
              fontSize: 12.5,
              cursor: 'pointer',
            }}
          >
            Filter zurücksetzen
          </button>
        </div>
      </Card>

      <Card style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: CO.muted }}>
            <span className="num" style={{ fontWeight: 600, color: CO.text }}>
              {fmtNum(rows.length)}
            </span>{' '}
            Einträge · gefiltert aus{' '}
            <span className="num">{fmtNum(fe.length)}</span> im Zeitraum
          </div>
        </div>
        <div
          style={{
            overflowX: 'auto',
            border: `1px solid ${CO.border}`,
            borderRadius: 10,
            maxHeight: '58vh',
            overflowY: 'auto',
          }}
        >
          {rows.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  <Th label="Datum" keyName="iso" />
                  <Th label="Uhrzeit" />
                  <Th label="Spielart" keyName="spielart" />
                  <Th label="Status" keyName="status" />
                  <Th label="Einsatz" keyName="einsatz" align="right" />
                  <Th label="Auszahlung" keyName="gewinn" align="right" />
                  <Th label="Nettoergebnis" keyName="netto" align="right" />
                  <Th label="Ergebnistyp" />
                  <Th label="ID" />
                  <Th label="" align="right" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((e: Eintrag) => (
                  <tr
                    key={e.id}
                    onClick={() => setDetailId(e.id)}
                    style={{
                      borderTop: `1px solid ${CO.border2}`,
                      cursor: 'pointer',
                      background: detailId === e.id ? '#eef5ef' : 'transparent',
                    }}
                  >
                    <td className="num" style={{ padding: '9px 10px', color: CO.text, whiteSpace: 'nowrap' }}>
                      {dstr(e.iso)}
                    </td>
                    <td className="num" style={{ padding: '9px 10px', color: CO.muted }}>
                      {tstr(e.iso)}
                    </td>
                    <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: artColor(e.spielart) }} />
                        {e.spielart}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <StatusBadge s={e.status} />
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                      <EurCell value={e.einsatz} tone="einsatz" />
                    </td>
                    <td
                      className="num"
                      style={{
                        padding: '9px 10px',
                        textAlign: 'right',
                        color: e.gewinn > 0 ? CO.auszahlung : CO.faint,
                      }}
                    >
                      {eur(e.gewinn)}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                      <NetCell n={e.gewinn - e.einsatz} />
                    </td>
                    <td style={{ padding: '9px 10px', color: CO.muted, textTransform: 'capitalize' }}>{e.typ}</td>
                    <td className="num" style={{ padding: '9px 10px', color: CO.faint }}>
                      {e.id}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                      <span style={{ color: CO.sidebar, fontWeight: 600, fontSize: 12 }}>Details ›</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 48, textAlign: 'center', color: CO.faint }}>
              Keine Einträge entsprechen den aktuellen Filtern.
            </div>
          )}
        </div>

        {pages > 1 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
            }}
          >
            <div style={{ fontSize: 12.5, color: CO.muted }}>
              Seite {page} von {pages}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setTPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${CO.border}`,
                  borderRadius: 7,
                  background: '#fff',
                  color: page <= 1 ? CO.faint : CO.text,
                  fontSize: 12.5,
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ‹ Zurück
              </button>
              <button
                onClick={() => setTPage(Math.min(pages, page + 1))}
                disabled={page >= pages}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${CO.border}`,
                  borderRadius: 7,
                  background: '#fff',
                  color: page >= pages ? CO.faint : CO.text,
                  fontSize: 12.5,
                  cursor: page >= pages ? 'not-allowed' : 'pointer',
                }}
              >
                Weiter ›
              </button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
